/**
 * Glossary Tooltip Context
 * Story 13.2: Glossário - Task 8 - AC#6
 *
 * Provides global tooltip integration:
 * - Fetches all active terms on app load (only when authenticated)
 * - Caches terms for 30 minutes
 * - Provides termsMap for instant tooltip lookup
 * - Auto-invalidates on term edits (admin)
 *
 * Usage:
 * 1. Wrap App with <GlossaryTooltipProvider>
 * 2. Use useGlossaryTooltips() in components to access termsMap
 * 3. Wrap text with <GlossaryTooltip>{content}</GlossaryTooltip>
 */
import React, { createContext, useContext, ReactNode, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import glossaryService, { GlossaryTerm } from "@/services/glossaryService";
import { useAuth } from "@/contexts/AuthContext";

interface GlossaryTooltipContextType {
  termsMap: Map<string, GlossaryTerm>;
  isLoading: boolean;
  error: Error | null;
}

const GlossaryTooltipContext = createContext<GlossaryTooltipContextType | undefined>(undefined);

interface GlossaryTooltipProviderProps {
  children: ReactNode;
}

export function GlossaryTooltipProvider({ children }: GlossaryTooltipProviderProps) {
  const queryClient = useQueryClient();
  const { isAuthenticated, user } = useAuth();

  // Fetch all active terms for tooltips (only when authenticated)
  const {
    data: terms = [],
    isLoading,
    error,
  } = useQuery<GlossaryTerm[], Error>({
    queryKey: ["glossary", "tooltips"],
    queryFn: () => glossaryService.getTerms({ limit: 200 }), // Fetch up to 200 active terms
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
    // Não dispara enquanto a troca de senha obrigatória estiver pendente: o gate
    // server-side 403a toda rota protegida até a senha ser trocada — evita ruído
    // de 403 no console na tela /force-change-password.
    enabled: isAuthenticated && !user?.password_change_required,
  });

  // Build termsMap for O(1) lookup
  const termsMap = new Map<string, GlossaryTerm>(
    terms.map((term) => [term.term.toLowerCase(), term])
  );

  // Listen for glossary updates (when admin edits terms)
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      // If any glossary query is updated, invalidate tooltips cache
      if (
        event?.query.queryKey[0] === "glossary" &&
        event.type === "updated" &&
        event?.query.queryKey[1] !== "tooltips" // Don't invalidate self
      ) {
        console.log("📝 Glossary updated - Refreshing tooltips cache");
        queryClient.invalidateQueries({ queryKey: ["glossary", "tooltips"] });
      }
    });

    return () => unsubscribe();
  }, [queryClient]);

  const value: GlossaryTooltipContextType = {
    termsMap,
    isLoading,
    error: error || null,
  };

  return (
    <GlossaryTooltipContext.Provider value={value}>{children}</GlossaryTooltipContext.Provider>
  );
}

/**
 * Hook to access glossary tooltips context
 * Returns termsMap for instant term lookup
 *
 * Example:
 * const { termsMap } = useGlossaryTooltips();
 * const termInfo = termsMap.get('terminal');
 * if (termInfo) {
 *   showTooltip(termInfo.short_definition || termInfo.definition);
 * }
 */
export function useGlossaryTooltips(): GlossaryTooltipContextType {
  const context = useContext(GlossaryTooltipContext);
  if (!context) {
    throw new Error("useGlossaryTooltips must be used within GlossaryTooltipProvider");
  }
  return context;
}
